/**
 * @file form-template/proofread.ts
 * @description 表單校對窗口用的純函式（2026-09-25 回報序42）。
 *
 * 把 zhcert-form/v1 表單 schema 攤平成「逐條校對清單」，並可把校對時修正的文字
 * 套回 schema（產生新版本）。前端校對抽屜與後端校對紀錄（條文數）共用。
 *
 * 段落規則：以表單的 sections（段落）為分組；條文編號沿用 checklist 的 clause.code，
 * 一般欄位以「段落序.欄位序」編號（如 2.3），確保校對紀錄可對照原表段落。
 *
 * ⚠️ shared 層不依賴 @zhcb/forms 型別，這裡以寬鬆結構讀取 schema（未知欄位原樣保留）。
 */
/** 安全轉字串 */
function str(v) {
    if (typeof v === 'string')
        return v;
    if (typeof v === 'number' || typeof v === 'boolean')
        return String(v);
    return '';
}
/**
 * 攤平表單 schema 為逐條校對清單。
 * @param schema form_template_versions.schema_json
 */
export function flattenSchemaForProofread(schema) {
    const s = (schema ?? {});
    const items = [];
    if (str(s.title)) {
        items.push({
            key: 'title',
            sectionIndex: -1,
            sectionTitle: '表單標題',
            no: '—',
            kind: 'form-title',
            text: str(s.title),
        });
    }
    (s.sections ?? []).forEach((sec, si) => {
        const secTitle = str(sec.title) || `段落 ${si + 1}`;
        const secNo = String(si + 1);
        items.push({
            key: `s${si}.title`,
            sectionIndex: si,
            sectionTitle: secTitle,
            no: secNo,
            kind: 'section-title',
            text: str(sec.title),
        });
        if (str(sec.description)) {
            items.push({
                key: `s${si}.desc`,
                sectionIndex: si,
                sectionTitle: secTitle,
                no: secNo,
                kind: 'section-desc',
                text: str(sec.description),
            });
        }
        (sec.fields ?? []).forEach((f, fi) => {
            const fNo = `${secNo}.${fi + 1}`;
            const base = `s${si}.f${fi}`;
            items.push({
                key: `${base}.label`,
                sectionIndex: si,
                sectionTitle: secTitle,
                no: fNo,
                kind: 'field-label',
                text: str(f.label),
            });
            (f.options ?? []).forEach((o, oi) => {
                items.push({
                    key: `${base}.opt${oi}`,
                    sectionIndex: si,
                    sectionTitle: secTitle,
                    no: `${fNo}(${oi + 1})`,
                    kind: 'option',
                    text: str(o.label),
                });
            });
            (f.columns ?? []).forEach((col, ci) => {
                items.push({
                    key: `${base}.col${ci}`,
                    sectionIndex: si,
                    sectionTitle: secTitle,
                    no: `${fNo}[${ci + 1}]`,
                    kind: 'column',
                    text: str(col.label),
                });
            });
            (f.groups ?? []).forEach((g, gi) => {
                items.push({
                    key: `${base}.g${gi}.title`,
                    sectionIndex: si,
                    sectionTitle: secTitle,
                    no: `${fNo}-${gi + 1}`,
                    kind: 'group-title',
                    text: str(g.title),
                });
                if (str(g.subTitle)) {
                    items.push({
                        key: `${base}.g${gi}.sub`,
                        sectionIndex: si,
                        sectionTitle: secTitle,
                        no: `${fNo}-${gi + 1}`,
                        kind: 'group-subtitle',
                        text: str(g.subTitle),
                    });
                }
                (g.clauses ?? []).forEach((c, ci) => {
                    items.push({
                        key: `${base}.g${gi}.c${ci}`,
                        sectionIndex: si,
                        sectionTitle: secTitle,
                        no: str(c.code) || `${fNo}-${gi + 1}.${ci + 1}`,
                        kind: 'clause',
                        text: str(c.text),
                    });
                });
            });
        });
    });
    return items;
}
/**
 * 把校對修正（key → 新文字）套回 schema，回傳深拷貝後的新 schema（不改動原物件）。
 * 無法解析的 key 會被忽略。
 *
 * @param schema 原 schema
 * @param edits 修正內容
 * @returns 新 schema 與實際套用的筆數
 */
export function applyProofreadEdits(schema, edits) {
    const next = JSON.parse(JSON.stringify(schema ?? {}));
    const s = next;
    let applied = 0;
    for (const [key, value] of Object.entries(edits)) {
        if (key === 'title') {
            s.title = value;
            applied++;
            continue;
        }
        const m = /^s(\d+)\.(?:(title|desc)|f(\d+)\.(?:(label)|opt(\d+)|col(\d+)|g(\d+)\.(?:(title|sub)|c(\d+))))$/.exec(key);
        if (!m)
            continue;
        const sec = s.sections?.[Number(m[1])];
        if (!sec)
            continue;
        if (m[2] === 'title')
            sec.title = value;
        else if (m[2] === 'desc')
            sec.description = value;
        else {
            const field = sec.fields?.[Number(m[3])];
            if (!field)
                continue;
            if (m[4])
                field.label = value;
            else if (m[5] !== undefined) {
                const opt = field.options?.[Number(m[5])];
                if (!opt)
                    continue;
                opt.label = value;
            }
            else if (m[6] !== undefined) {
                const col = field.columns?.[Number(m[6])];
                if (!col)
                    continue;
                col.label = value;
            }
            else if (m[7] !== undefined) {
                const g = field.groups?.[Number(m[7])];
                if (!g)
                    continue;
                if (m[8] === 'title')
                    g.title = value;
                else if (m[8] === 'sub')
                    g.subTitle = value;
                else if (m[9] !== undefined) {
                    const clause = g.clauses?.[Number(m[9])];
                    if (!clause)
                        continue;
                    clause.text = value;
                }
                else
                    continue;
            }
            else
                continue;
        }
        applied++;
    }
    return { schema: next, applied };
}
